import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import FileUploader from "../uploader/fileUploader";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { downloadBoxFile } from "../utilities/downloadFiles";
import { BoxFile } from "../../types/BoxFile";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";
import { useParams } from "react-router-dom";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
// import { fetchCaseById } from "../../store/user/userSlice";
import {
  deleteBoxFiles,
  resetDeleteState,
} from "../../store/common/deleteBoxFileSlice";
import ConfirmModal from "../utilities/confirmModal";
import { useToast } from "../utilities/Toast";
import FileCheckStatus from "./FileCheckStatus";
import { isPreviewOrPhotorealisticFileType } from "../../utils/cases/photorealisticScan";
import { formatDateTimeIST } from "../../utils/dateTime";

const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="h-4 w-4 shrink-0"
    aria-hidden
  >
    <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
    <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
  </svg>
);

const Previews = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { caseFiles, fetching } = useAppSelector((s) => s.fileUpload);
  const { user, boxAccessToken } = useAppSelector((s) => s.auth);
  const { employee } = useAppSelector((s) => s.allocationAuth);
  const { selectedCase, isLoading } = useAppSelector((s) => s.adminCases);
  const { access } = useAppSelector((s) => s.tsUploadAccess);
  const {
    loading: deleting,
    success: deleteSuccess,
    error: deleteError,
  } = useAppSelector((s) => s.deleteBoxFiles);
  const { case_id } = useParams<{ case_id: string }>();

  const [selectedClientUploadFile, setSelectedClientUploadFile] = useState("");
  const [downloadingFiles, setDownloadingFiles] = useState<number[]>([]);
  const [downloadErrors, setDownloadErrors] = useState<number[]>([]);
  const [downloadedFiles, setDownloadedFiles] = useState<number[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(
    null,
  );
  const loadedImageIds = useRef<Set<number>>(new Set());
  const [loadedVersion, setLoadedVersion] = useState(0);

  useEffect(() => {
    if (!case_id) return;

    const fetchAll = async () => {
      try {
        await Promise.all([
          dispatch(fetchBoxAccessToken()),
          // dispatch(fetchCaseById(case_id)),
          dispatch(fetchCaseFilesByCaseId({ case_id })),
        ]);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAll();
  }, [case_id, dispatch]);

  // ─── Derived file lists ───
  const previewFiles = useMemo(
    () =>
      caseFiles.filter((f) => isPreviewOrPhotorealisticFileType(f.file_type)),
    [caseFiles],
  );

  const uniqueFolders = useMemo(() => {
    const folders = caseFiles
      .filter((f) => f.case_id === case_id && f.folder_name)
      .map((f) => f.folder_name);
    return Array.from(new Set(folders));
  }, [caseFiles, case_id]);

  const imageFiles = useMemo(
    () =>
      previewFiles.filter((f) =>
        /\.(jpg|jpeg|png|gif|webp)$/i.test(f.file_name),
      ),
    [previewFiles],
  );

  const filesByFolder = useMemo(() => {
    return previewFiles.reduce<Record<string, BoxFile[]>>((acc, f) => {
      const folder = f.folder_name || "Unknown";
      (acc[folder] ||= []).push(f);
      return acc;
    }, {});
  }, [previewFiles]);

  const getThumbnailUrl = useCallback(
    (id: number) =>
      `https://api.box.com/2.0/files/${id}/thumbnail.png?min_height=256&min_width=256&access_token=${boxAccessToken}`,
    [boxAccessToken],
  );

  const getFileUrl = useCallback(
    (id: number) =>
      `https://api.box.com/2.0/files/${id}/content?access_token=${boxAccessToken}`,
    [boxAccessToken],
  );

  const markImageLoaded = useCallback((fileId: number) => {
    if (!loadedImageIds.current.has(fileId)) {
      loadedImageIds.current.add(fileId);
      setLoadedVersion((v) => v + 1);
    }
  }, []);

  // ─── Preload images ───
  useEffect(() => {
    if (!boxAccessToken || imageFiles.length === 0) return;

    const preload = (fileId: number) => {
      if (loadedImageIds.current.has(fileId)) return;
      const img = new Image();
      img.decoding = "async";
      img.src = getFileUrl(fileId);
      img.onload = () => markImageLoaded(fileId);
    };

    if (currentImageIndex !== null) {
      preload(imageFiles[currentImageIndex].fileId);
    }

    imageFiles.forEach((file, idx) => {
      if (idx !== currentImageIndex) preload(file.fileId);
    });
  }, [
    boxAccessToken,
    imageFiles,
    currentImageIndex,
    getFileUrl,
    markImageLoaded,
  ]);

  // ─── Download handler ───
  const handleDownload = useCallback(
    async (fileId: number, fileName: string) => {
      try {
        setDownloadingFiles((prev) => [...prev, fileId]);
        setDownloadErrors((prev) => prev.filter((id) => id !== fileId));
        await downloadBoxFile(
          fileId.toString(),
          fileName,
          boxAccessToken || "",
        );
        setDownloadedFiles((prev) => [...prev, fileId]);
      } catch (err) {
        console.error(err);
        setDownloadErrors((prev) => [...prev, fileId]);
      } finally {
        setDownloadingFiles((prev) => prev.filter((id) => id !== fileId));
      }
    },
    [boxAccessToken],
  );

  // ─── Keyboard navigation ───
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (currentImageIndex === null) return;
      if (e.key === "ArrowLeft")
        setCurrentImageIndex((prev) =>
          prev === null
            ? 0
            : (prev - 1 + imageFiles.length) % imageFiles.length,
        );
      if (e.key === "ArrowRight")
        setCurrentImageIndex((prev) =>
          prev === null ? 0 : (prev + 1) % imageFiles.length,
        );
      if (e.key === "Escape") setCurrentImageIndex(null);
    },
    [currentImageIndex, imageFiles.length],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // ─── Delete Mode logic (same as ClientUploads) ───
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<number[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const toggleFileSelect = (fileId: number) => {
    setSelectedForDelete((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId],
    );
  };

  const getFileIdsInFolder = useCallback(
    (folder: string) => {
      return (filesByFolder[folder] || []).map((f) => f.fileId);
    },
    [filesByFolder],
  );

  const handleDeleteClick = () => {
    if (!selectedForDelete.length) return;
    setShowConfirmModal(true);
  };

  const allFileIds = useMemo(
    () => previewFiles.map((f) => f.fileId),
    [previewFiles],
  );

  const allSelectedGlobally = useMemo(
    () =>
      allFileIds.length > 0 &&
      allFileIds.every((id) => selectedForDelete.includes(id)),
    [allFileIds, selectedForDelete],
  );

  const toggleSelectAllGlobal = () => {
    if (allSelectedGlobally) {
      // Unselect ALL
      setSelectedForDelete([]);
    } else {
      // Select ALL
      setSelectedForDelete([...allFileIds]);
    }
  };

  const handleConfirmDelete = async () => {
    setShowConfirmModal(false);
    await dispatch(
      deleteBoxFiles({
        fileIds: selectedForDelete.map(String),
        deleteBy:
          user?.role === "Super Admin"
            ? user?.user_name
            : employee?.emp_name || "",
      }),
    );
    setSelectedForDelete([]);
  };

  useEffect(() => {
    if (deleteSuccess && case_id) {
      dispatch(fetchCaseFilesByCaseId({ case_id }));
      dispatch(resetDeleteState());
      setDeleteMode(false);
      showToast("File(s) deleted successfully!", "success");
    }
  }, [deleteSuccess, dispatch, case_id, showToast]);

  useEffect(() => {
    if (deleteError) {
      showToast(`Failed to delete files: ${deleteError}`, "error");
      dispatch(resetDeleteState());
    }
  }, [deleteError, dispatch, showToast]);

  if (isLoading || fetching)
    return <p className="text-gray-500 italic">Loading previews...</p>;

  const currentFullscreenFile =
    currentImageIndex !== null ? imageFiles[currentImageIndex] : null;
  const isCurrentFullscreenLoaded = currentFullscreenFile
    ? loadedImageIds.current.has(currentFullscreenFile.fileId)
    : false;
  const isFullscreenDownloading = currentFullscreenFile
    ? downloadingFiles.includes(currentFullscreenFile.fileId)
    : false;
  const isFullscreenDownloaded = currentFullscreenFile
    ? downloadedFiles.includes(currentFullscreenFile.fileId)
    : false;
  const isFullscreenDownloadError = currentFullscreenFile
    ? downloadErrors.includes(currentFullscreenFile.fileId)
    : false;
  void loadedVersion;

  return (
    <div className="w-full min-h-[60vh] flex flex-col text-sm md:flex-row mx-auto p-4 gap-6">
      {/* Left: Previews */}
      <div className="w-full md:w-1/2 flex flex-col gap-4 text-gray-800">
        {/* Delete Controls */}

        {previewFiles && previewFiles.length > 0 ? (
          <div className="text-white">
            {user &&
              !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
                <div className="flex mb-2 space-x-2">
                  {!deleteMode ? (
                    <button
                      onClick={() => setDeleteMode(true)}
                      className="bg-red-500 px-4 py-1 rounded-lg hover:bg-red-600 cursor-pointer"
                    >
                      Delete Files
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleDeleteClick}
                        disabled={deleting || selectedForDelete.length === 0}
                        className="bg-green-500 px-4 py-1 rounded-lg hover:bg-green-600 cursor-pointer"
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => {
                          setDeleteMode(false);
                          setSelectedForDelete([]);
                        }}
                        className="bg-gray-500 py-1 px-4 rounded-lg hover:bg-gray-600 cursor-pointer"
                      >
                        Cancel
                      </button>
                      {deleteMode && (
                        <label className="flex items-center gap-2 ml-4 mt-2 text-sm text-black">
                          <input
                            type="checkbox"
                            checked={allSelectedGlobally}
                            onChange={toggleSelectAllGlobal}
                          />
                          Select All Files
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}
          </div>
        ) : null}

        {/* Folder Filter */}
        {uniqueFolders.length > 0 &&
          selectedCase?.service_type === "filledOrder" && (
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Select Patient Folder -- {uniqueFolders.length} folder
              </label>
              <select
                className="w-full border rounded-md px-3 py-2 text-gray-700 cursor-pointer"
                value={selectedClientUploadFile}
                onChange={(e) => setSelectedClientUploadFile(e.target.value)}
              >
                <option value="">All patients</option>
                {uniqueFolders.map((folder, i) => (
                  <option key={i} value={folder}>
                    {folder}
                  </option>
                ))}
              </select>
            </div>
          )}

        {/* Files Display */}
        {Object.entries(filesByFolder)
          .filter(
            ([folder]) =>
              !selectedClientUploadFile || folder === selectedClientUploadFile,
          )
          .map(([folder, files]) => {
            const fileIdsInFolder = getFileIdsInFolder(folder);
            const allSelectedInFolder = fileIdsInFolder.every((id) =>
              selectedForDelete.includes(id),
            );

            const toggleSelectAllInFolder = () => {
              if (allSelectedInFolder) {
                setSelectedForDelete((prev) =>
                  prev.filter((id) => !fileIdsInFolder.includes(id)),
                );
              } else {
                setSelectedForDelete((prev) => [
                  ...new Set([...prev, ...fileIdsInFolder]),
                ]);
              }
            };

            return (
              <div key={folder}>
                <h3 className="font-bold mb-1 flex items-center gap-2">
                  {deleteMode && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={allSelectedInFolder}
                        onChange={toggleSelectAllInFolder}
                      />
                      Select All in
                    </label>
                  )}
                  {folder}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {files.map((f) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                      f.file_name,
                    );
                    const isDownloading = downloadingFiles.includes(f.fileId);
                    const isError = downloadErrors.includes(f.fileId);
                    const isDownloaded = downloadedFiles.includes(f.fileId);
                    const isSelected = selectedForDelete.includes(f.fileId);

                    if (isImage) {
                      const imgIdx = imageFiles.findIndex(
                        (img) => img.fileId === f.fileId,
                      );
                      return (
                        <div
                          key={f.fileId}
                          className={`relative group rounded-lg ${
                            deleteMode && isSelected ? "bg-red-100" : ""
                          }`}
                        >
                          {deleteMode && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleFileSelect(f.fileId)}
                              className="absolute top-1 left-1 z-10"
                            />
                          )}
                          <img
                            src={getThumbnailUrl(f.fileId)}
                            alt={f.file_name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-32 object-cover rounded-lg cursor-pointer transform transition-transform duration-200 group-hover:scale-105"
                            onClick={() =>
                              !deleteMode && setCurrentImageIndex(imgIdx)
                            }
                            onError={(e) =>
                              ((e.target as HTMLImageElement).src = getFileUrl(
                                f.fileId,
                              ))
                            }
                          />
                          {!deleteMode && (
                            <button
                              onClick={() =>
                                handleDownload(f.fileId, f.file_name)
                              }
                              className="absolute bottom-1 left-1 right-1 text-left text-xs bg-black/50 text-white px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition"
                            >
                              <span className="block truncate">{f.file_name}</span>
                              {f.creation_ts && (
                                <span className="block text-[10px] text-white/80">
                                  Uploaded: {formatDateTimeIST(f.creation_ts)} IST
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={f.fileId}
                        className={`w-full flex py-0.5 items-center gap-2 ${
                          deleteMode && isSelected ? "bg-red-100" : ""
                        }`}
                      >
                        {deleteMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleFileSelect(f.fileId)}
                          />
                        )}
                        <button
                          onClick={() =>
                            !deleteMode && handleDownload(f.fileId, f.file_name)
                          }
                          className={`w-full text-start ${
                            deleteMode
                              ? "text-gray-700"
                              : "text-blue-600 hover:underline"
                          }`}
                          disabled={isDownloading || deleteMode}
                        >
                          <span className="block">{f.file_name}</span>
                          {f.creation_ts && (
                            <span className="block text-[11px] text-gray-500 no-underline">
                              Uploaded: {formatDateTimeIST(f.creation_ts)} IST
                            </span>
                          )}
                        </button>
                        {isDownloading && (
                          <span className="text-gray-500 italic">
                            Downloading...
                          </span>
                        )}
                        {isError && (
                          <span className="text-red-500 italic">Failed</span>
                        )}
                        {isDownloaded && (
                          <span className="text-green-500 italic">
                            Downloaded
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

        {!fetching && previewFiles.length === 0 && (
          <p className="text-gray-500 italic">No previews uploaded yet.</p>
        )}
      </div>

      {/* Right: Upload */}
      <div className="w-1/2 flex-1 space-y-6">
        {selectedCase?.status === "Files pending" ? (
          <p className="text-red-400">Please upload client files.</p>
        ) : selectedCase?.service_type === "filledOrder" &&
          uniqueFolders.length === 0 ? (
          <p className="text-orange-500 font-medium">
            Multiple-unit case — create a TS folder for upload.
          </p>
        ) : (
          user &&
          !["Admin Read", "Production", "Side Admin"].includes(user.role) && (
            <FileUploader
              userId={user.user_id || 0}
              caseId={case_id || ""}
              caseOwner={(selectedCase?.case_owner || "").replace(/,/g, " ")}
              caseFolderId={selectedCase?.box_folder_id?.toString() || ""}
              file_type="TS_PREVIEW"
              accessToken={boxAccessToken || ""}
              patientFolderName={(selectedClientUploadFile || "")
                .replace(/\.zip$/i, "")
                .replace(/,/g, " ")
                .replace(/\s+/g, " ")
                .trim()}
              disabled={
                user.role !== "Super Admin" &&
                access?.allowed !== true &&
                selectedCase?.status?.toLowerCase() !== "completed"
              }
              disabledReason={access?.message}
            />
          )
        )}
        <FileCheckStatus caseId={case_id || selectedCase?.case_id} />
      </div>

      {/* Fullscreen Viewer */}
      {currentImageIndex !== null && (
        <div
          className="fixed inset-0 z-[80] flex flex-col bg-black/90"
          onClick={() => setCurrentImageIndex(null)}
        >
          <div
            className="z-30 flex shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-zinc-950 px-4 py-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0 text-white">
              <p className="break-all text-base font-medium">
                {imageFiles[currentImageIndex].file_name}
              </p>
              {imageFiles[currentImageIndex].creation_ts && (
                <p className="mt-0.5 text-xs font-normal text-white/80">
                  Uploaded:{" "}
                  {formatDateTimeIST(imageFiles[currentImageIndex].creation_ts)} IST
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!currentFullscreenFile) return;
                  handleDownload(
                    currentFullscreenFile.fileId,
                    currentFullscreenFile.file_name,
                  );
                }}
                disabled={isFullscreenDownloading || !currentFullscreenFile}
                className="flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Download image"
              >
                {isFullscreenDownloading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Downloading...
                  </>
                ) : isFullscreenDownloaded ? (
                  "Downloaded"
                ) : isFullscreenDownloadError ? (
                  "Retry download"
                ) : (
                  <>
                    <DownloadIcon />
                    Download
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setCurrentImageIndex(null)}
                className="text-3xl font-bold leading-none text-white hover:text-gray-300"
                aria-label="Close fullscreen"
              >
                &times;
              </button>
            </div>
          </div>
          <div
            className="relative flex min-h-0 flex-1 items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex((prev) =>
                  prev === null
                    ? 0
                    : (prev - 1 + imageFiles.length) % imageFiles.length,
                );
              }}
              className="absolute left-3 z-20 px-2 py-1 text-4xl text-white hover:text-gray-300"
              aria-label="Previous image"
            >
              &#10094;
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex((prev) =>
                  prev === null ? 0 : (prev + 1) % imageFiles.length,
                );
              }}
              className="absolute right-3 z-20 px-2 py-1 text-4xl text-white hover:text-gray-300"
              aria-label="Next image"
            >
              &#10095;
            </button>
            <div className="relative h-full w-full">
              {imageFiles.map((file, idx) => (
                <img
                  key={file.fileId}
                  src={getFileUrl(file.fileId)}
                  alt={file.file_name}
                  decoding="async"
                  fetchPriority={idx === currentImageIndex ? "high" : "low"}
                  className={`absolute inset-0 m-auto max-h-full max-w-[calc(100%-5rem)] object-contain transition-opacity duration-150 ${
                    idx === currentImageIndex
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                  }`}
                  onLoad={() => markImageLoaded(file.fileId)}
                  aria-hidden={idx !== currentImageIndex}
                />
              ))}
              {!isCurrentFullscreenLoaded && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
        }}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default Previews;